export type CaptureMode = 'audio-video' | 'audio-only' | 'unsupported'

export interface CapturePreparation {
  supported: boolean
  mode: CaptureMode
  stream: MediaStream | null
  trackKinds: string[]
}

export function createLocalCapture(canvas: HTMLCanvasElement, audioStream: MediaStream | null): CapturePreparation {
  const sourceAudioTracks = audioStream?.getAudioTracks?.() ?? audioStream?.getTracks().filter((track) => track.kind === 'audio') ?? []
  const audioTracks = sourceAudioTracks.map((track) => typeof track.clone === 'function' ? track.clone() : track)
  let videoTracks: MediaStreamTrack[] = []
  if (typeof canvas.captureStream === 'function') {
    try {
      videoTracks = canvas.captureStream(30).getVideoTracks()
    } catch {
      videoTracks = []
    }
  }
  if (audioTracks.length === 0) {
    videoTracks.forEach((track) => track.stop())
    return { supported: false, mode: 'unsupported', stream: null, trackKinds: [] }
  }
  const tracks = [...videoTracks, ...audioTracks]
  if (tracks.length === 0 || typeof MediaStream === 'undefined') {
    return { supported: false, mode: 'unsupported', stream: null, trackKinds: [] }
  }
  const stream = new MediaStream(tracks)
  return {
    supported: true,
    mode: videoTracks.length > 0 ? 'audio-video' : 'audio-only',
    stream,
    trackKinds: tracks.map((track) => track.kind)
  }
}

export interface LocalRecording {
  blob: Blob
  url: string
  extension: 'webm' | 'mp4' | 'ogg'
  mode: CaptureMode
}

export class LocalCaptureController {
  private recorder: MediaRecorder | null = null
  private chunks: Blob[] = []
  private prepared: CapturePreparation | null = null
  private operationId = 0
  private stopping = false

  constructor(private readonly canvas: HTMLCanvasElement, private readonly audioStream: () => MediaStream | null) {}

  start(): { started: boolean; mode: CaptureMode } {
    if (this.recorder || this.stopping || typeof MediaRecorder === 'undefined') return { started: false, mode: 'unsupported' }
    const prepared = createLocalCapture(this.canvas, this.audioStream())
    if (!prepared.stream) return { started: false, mode: prepared.mode }
    try {
      const mimeType = chooseMimeType(prepared.mode)
      this.chunks = []
      this.prepared = prepared
      this.operationId += 1
      const operationId = this.operationId
      this.recorder = new MediaRecorder(prepared.stream, mimeType ? { mimeType } : undefined)
      this.recorder.addEventListener('dataavailable', (event) => {
        if (operationId === this.operationId && event.data.size > 0) this.chunks.push(event.data)
      })
      this.recorder.start(250)
      return { started: true, mode: prepared.mode }
    } catch {
      prepared.stream.getTracks().forEach((track) => track.stop())
      this.prepared = null
      this.recorder = null
      return { started: false, mode: 'unsupported' }
    }
  }

  stop(): Promise<LocalRecording> {
    return new Promise((resolve, reject) => {
      const recorder = this.recorder
      const prepared = this.prepared
      const operationId = this.operationId
      if (!recorder || !prepared) {
        reject(new Error('Recording was not active.'))
        return
      }
      this.recorder = null
      this.prepared = null
      this.stopping = true
      let settled = false
      const cleanup = (): void => {
        prepared.stream?.getTracks().forEach((track) => track.stop())
        if (operationId === this.operationId) this.chunks = []
        this.stopping = false
      }
      recorder.addEventListener('stop', () => {
        if (settled) return
        if (operationId !== this.operationId) {
          settled = true
          cleanup()
          reject(new Error('Recording was canceled.'))
          return
        }
        settled = true
        const type = recorder.mimeType || this.chunks[0]?.type || (prepared.mode === 'audio-only' ? 'audio/ogg' : 'video/webm')
        const blob = new Blob(this.chunks, { type })
        const extension: LocalRecording['extension'] = type.includes('mp4') ? 'mp4' : type.includes('ogg') ? 'ogg' : 'webm'
        cleanup()
        resolve({ blob, url: URL.createObjectURL(blob), extension, mode: prepared.mode })
      }, { once: true })
      recorder.addEventListener('error', () => {
        if (settled) return
        settled = true
        cleanup()
        reject(new Error('Local capture failed.'))
      }, { once: true })
      try {
        recorder.stop()
      } catch {
        settled = true
        cleanup()
        reject(new Error('Local capture could not stop.'))
      }
    })
  }

  cancel(): void {
    this.operationId += 1
    const recorder = this.recorder
    this.recorder = null
    if (recorder?.state !== 'inactive') recorder?.stop()
    this.prepared?.stream?.getTracks().forEach((track) => track.stop())
    this.prepared = null
    this.chunks = []
  }
}

function chooseMimeType(mode: CaptureMode): string {
  if (typeof MediaRecorder.isTypeSupported !== 'function') return ''
  const candidates = mode === 'audio-only'
    ? ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/mp4']
    : ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? ''
}
